import React from 'react';
import ModalShell from '../../shared/components/modal/ModalShell';
import ModalFormFooter from '../../shared/components/modal/ModalFormFooter';
import FormField from '../../shared/components/modal/FormField';
import NestedListEditor from '../../shared/components/modal/NestedListEditor';
import useFormState from '../../shared/hooks/useFormState';
import { trimData } from '../../shared/lib/formatters';

const requiredFields = [
  { name: 'name', required: true },
  { name: 'generation_name', required: true },
];

const buildPayload = (data) => ({
  name: data.name,
  generation_name: data.generation_name,
  speaker_labels: data.speaker_labels,
  in_service: data.in_service,
  position: data.position,
  fields: data.fields
    .filter((f) => f.name.trim() !== '')
    .map((f) => (f.id ? { id: f.id, name: f.name, position: f.position } : { name: f.name, position: f.position })),
  inputs: data.inputs
    .filter((i) => i.type.trim() !== '')
    .map((i) => (i.id ? { id: i.id, type: i.type } : { type: i.type })),
  outputs: data.outputs
    .filter((o) => o.type.trim() !== '')
    .map((o) => (o.id ? { id: o.id, type: o.type } : { type: o.type })),
});

const ToolsFormModal = ({ isOpen, onClose, mode = 'create', initialValues, onSubmit }) => {
  const { formData, setField, isLoading, setIsLoading, isCreateDisabled, isSubmitDisabled } = useFormState({
    initialValues,
    isOpen,
    mode,
  });

  const handleCancel = () => onClose();

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await onSubmit(trimData(buildPayload(formData)));
    } finally {
      setIsLoading(false);
    }
  };

  const disabled = isLoading || (mode === 'create' ? isCreateDisabled(requiredFields) : isSubmitDisabled);

  const footer = (
    <ModalFormFooter
      onCancel={handleCancel}
      onSubmit={handleSave}
      isLoading={isLoading}
      isDisabled={disabled}
      submitLabel={mode === 'edit' ? 'Save Changes' : 'Create'}
      loadingLabel={mode === 'edit' ? 'Saving...' : 'Creating...'}
    />
  );

  if (!formData) return null;

  const fields = formData.fields || [];
  const inputs = formData.inputs || [];
  const outputs = formData.outputs || [];

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleCancel}
      title={mode === 'edit' ? 'Edit Tool' : 'Create Tool'}
      footer={footer}
      maxWidthClass="max-w-4xl"
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="flex flex-col gap-4">
          <FormField
            field={{ name: 'name', label: 'Name', type: 'text', required: true }}
            value={formData.name}
            onChange={(e) => setField('name', e.target.value)}
          />
          <div className="grid grid-cols-2 gap-4">
            <FormField
              field={{ name: 'generation_name', label: 'Generation Name', type: 'text', required: true }}
              value={formData.generation_name}
              onChange={(e) => setField('generation_name', e.target.value)}
            />
            <FormField
              field={{ name: 'position', label: 'Position', type: 'number', required: true }}
              value={formData.position}
              onChange={(e) => setField('position', parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <FormField
              field={{ name: 'speaker_labels', label: 'Speaker Labels', type: 'checkbox' }}
              value={formData.speaker_labels}
              onChange={(e) => setField('speaker_labels', e.target.checked)}
            />
            <FormField
              field={{ name: 'in_service', label: 'In Service', type: 'checkbox' }}
              value={formData.in_service}
              onChange={(e) => setField('in_service', e.target.checked)}
            />
          </div>

          <div className="border-t pt-4">
            <NestedListEditor
              label="Tool Fields"
              addLabel="Add Field"
              items={fields}
              fields={[
                { name: 'name', label: 'Field Name', placeholder: 'Field Name' },
                { name: 'position', label: 'Position', type: 'number', placeholder: 'Position' },
              ]}
              onAdd={() =>
                setField('fields', [...fields, { name: '', position: fields.length + 1 }])
              }
              onRemove={(index) => setField('fields', fields.filter((_, i) => i !== index))}
              onChange={(index, key, value) => {
                const next = [...fields];
                next[index] = { ...next[index], [key]: value };
                setField('fields', next);
              }}
            />
          </div>

          <div className="border-t pt-4">
            <NestedListEditor
              label="Tool Inputs"
              addLabel="Add Input"
              items={inputs}
              fields={[{ name: 'type', label: 'Type', placeholder: 'Input Type' }]}
              onAdd={() => setField('inputs', [...inputs, { type: '' }])}
              onRemove={(index) => setField('inputs', inputs.filter((_, i) => i !== index))}
              onChange={(index, key, value) => {
                const next = [...inputs];
                next[index] = { ...next[index], [key]: value };
                setField('inputs', next);
              }}
            />
          </div>

          <div className="border-t pt-4">
            <NestedListEditor
              label="Tool Outputs"
              addLabel="Add Output"
              items={outputs}
              fields={[{ name: 'type', label: 'Type', placeholder: 'Output Type' }]}
              onAdd={() => setField('outputs', [...outputs, { type: '' }])}
              onRemove={(index) => setField('outputs', outputs.filter((_, i) => i !== index))}
              onChange={(index, key, value) => {
                const next = [...outputs];
                next[index] = { ...next[index], [key]: value };
                setField('outputs', next);
              }}
            />
          </div>
        </div>
      </form>
    </ModalShell>
  );
};

export default ToolsFormModal;
